import { computed, ref, type Ref } from 'vue';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  primaryAction?: 'confirm' | 'cancel';
}

interface ChooseOptions extends Omit<ConfirmOptions, 'primaryAction'> {
  /** Второй выход из диалога, например «Не сохранять». Esc и крестик — отмена. */
  alternativeText: string;
}

export type ChooseResult = 'confirm' | 'alternative' | 'cancel';

interface PromptOptions extends ConfirmOptions {
  inputLabel?: string;
  inputPlaceholder?: string;
  initialValue?: string;
  required?: boolean;
}

interface InternalConfirmOptions extends ConfirmOptions {
  alternativeText?: string;
  input?: {
    label: string;
    placeholder: string;
    required: boolean;
  };
}

const ALTERNATIVE = Symbol('alternative');

type SettledValue = boolean | string | null | typeof ALTERNATIVE;

interface ConfirmState {
  isOpen: Ref<boolean>;
  inputValue: Ref<string>;
  options: Ref<InternalConfirmOptions | null>;
  resolve: ((value: SettledValue) => void) | null;
}

const state: ConfirmState = {
  isOpen: ref(false),
  inputValue: ref(''),
  options: ref(null),
  resolve: null,
};

/** Завершает ожидающий вызов один раз. Новый диалог заменяет открытый: тот получает отмену, а не висит вечно. */
const settle = (value: SettledValue): void => {
  state.resolve?.(value);
  state.resolve = null;
};

/** Открывает диалог вместо текущего; `toResult` переводит способ закрытия в ответ вызывающему. */
const open = <T>(options: InternalConfirmOptions, toResult: (value: SettledValue) => T): Promise<T> =>
  new Promise((resolve) => {
    settle(null);
    state.options.value = options;
    state.isOpen.value = true;
    state.resolve = (value) => resolve(toResult(value));
  });

const withDefaults = (options: ConfirmOptions, confirmText: string): InternalConfirmOptions => ({
  title: options.title ?? 'Подтверждение',
  message: options.message,
  confirmText: options.confirmText ?? confirmText,
  cancelText: options.cancelText ?? 'Отмена',
  primaryAction: options.primaryAction ?? 'confirm',
});

export const useConfirm = () => {
  const confirm = (options: string | ConfirmOptions): Promise<boolean> =>
    open(withDefaults(typeof options === 'string' ? { message: options } : options, 'Да'), (value) => value === true);

  /** Диалог с двумя выходами и отменой: «Сохранить», «Не сохранять», Esc — остаться. */
  const choose = (options: ChooseOptions): Promise<ChooseResult> =>
    open({ ...withDefaults(options, 'Да'), alternativeText: options.alternativeText }, (value) =>
      value === true ? 'confirm' : value === ALTERNATIVE ? 'alternative' : 'cancel',
    );

  const prompt = (options: PromptOptions): Promise<string | null> => {
    state.inputValue.value = options.initialValue ?? '';

    return open(
      {
        ...withDefaults(options, 'Подтвердить'),
        input: {
          label: options.inputLabel ?? 'Причина',
          placeholder: options.inputPlaceholder ?? '',
          required: options.required ?? true,
        },
      },
      (value) => (typeof value === 'string' ? value : null),
    );
  };

  const handleConfirm = () => {
    if (state.options.value?.input) {
      const value = state.inputValue.value.trim();
      if (state.options.value.input.required && value === '') {
        return;
      }

      state.isOpen.value = false;
      settle(value);

      return;
    }

    state.isOpen.value = false;
    settle(true);
  };

  const handleAlternative = () => {
    state.isOpen.value = false;
    settle(ALTERNATIVE);
  };

  const handleCancel = () => {
    state.isOpen.value = false;
    settle(state.options.value?.input ? null : false);
  };

  const canConfirm = computed(() => !state.options.value?.input?.required || state.inputValue.value.trim() !== '');

  return {
    canConfirm,
    choose,
    confirm,
    inputValue: state.inputValue,
    isOpen: state.isOpen,
    options: state.options,
    prompt,
    handleConfirm,
    handleAlternative,
    handleCancel,
  };
};
